/**
 * useAccountDeletion Hook
 * Manages account deletion state and operations
 * Supports 30-day grace period with cancellation option
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { errorMessage } from '../generated/api/endpoint-errors';

export interface DeletionStatus {
  hasPendingRequest: boolean;
  requestId?: string;
  requestedAt?: string;
  scheduledFor?: string;
  daysRemaining?: number;
}

interface UseAccountDeletionResult {
  /** Current deletion request status */
  deletionStatus: DeletionStatus | null;
  /** Whether the initial status fetch is in progress */
  isFetchingStatus: boolean;
  /** Whether the initial status fetch failed */
  fetchError: boolean;
  /** Whether an API call is in progress */
  isLoading: boolean;
  /** Error message if the last operation failed */
  error: string | null;
  /** Request account deletion with optional reason */
  requestDeletion: (reason?: string) => Promise<boolean>;
  /** Cancel a pending deletion request */
  cancelDeletion: () => Promise<boolean>;
  /** Refresh the deletion status from the server */
  refreshStatus: () => Promise<void>;
}

export function useAccountDeletion(): UseAccountDeletionResult {
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  /**
   * Fetch current deletion status from the server
   */
  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setDeletionStatus(null);
      return;
    }

    setIsFetchingStatus(true);
    setFetchError(false);
    const result = await api.getDeletionStatus();
    if (result.ok) {
      setDeletionStatus(result.data);
      setError(null);
    } else {
      setFetchError(true);
      // Domain/transport errors are already captured by wrapError in endpoint-errors
    }
    setIsFetchingStatus(false);
  }, [isAuthenticated]);

  // Fetch status on mount and when auth state changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch with setState on response is intentional
    fetchStatus();
  }, [fetchStatus]);

  /**
   * Request account deletion with 30-day grace period
   */
  const requestDeletion = async (reason?: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    const result = await api.requestDeletion({ reason });
    if (result.ok) {
      setDeletionStatus({
        hasPendingRequest: true,
        requestId: result.data.requestId,
        scheduledFor: result.data.scheduledFor,
        daysRemaining: result.data.daysRemaining,
      });
      setIsLoading(false);
      return true;
    } else {
      setError(errorMessage(result));
      setIsLoading(false);
      return false;
    }
  };

  /**
   * Cancel a pending deletion request
   */
  const cancelDeletion = async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    const result = await api.cancelDeletion();
    if (result.ok) {
      setDeletionStatus({ hasPendingRequest: false });
      setIsLoading(false);
      return true;
    } else {
      setError(errorMessage(result));
      setIsLoading(false);
      return false;
    }
  };

  return {
    deletionStatus,
    isFetchingStatus,
    fetchError,
    isLoading,
    error,
    requestDeletion,
    cancelDeletion,
    refreshStatus: fetchStatus,
  };
}
